'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import type { GamesFiltersType, VolatilityFilterValue } from '../GamesFilter';

function useGameFilters() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [gameFiltersState, setGameFiltersState] = useState<GamesFiltersType>({});

  const getGamesFiltersFromUrl = useCallback((): GamesFiltersType => {
    const params = new URLSearchParams(searchParams);
    const urlFilters: GamesFiltersType = {};

    // Handle sorting
    const sort = params.get('sorting');
    if (sort) {
      urlFilters.sorting = sort;
    }

    // Handle volatility
    const minVol = params.get('volatilityMin');
    const maxVol = params.get('volatilityMax');
    if (minVol && maxVol) {
      urlFilters.volatility = {
        min: minVol as VolatilityFilterValue,
        max: maxVol as VolatilityFilterValue,
      };
    }

    // Handle gameProviders
    const providers = params.get('gameProviders')?.split(',') || [];
    if (providers.length > 0) {
      urlFilters.gameProviders = providers;
    }

    // Handle minimumBet
    const minBets = params.get('minimumBet')?.split(',') || [];
    if (minBets.length > 0) {
      urlFilters.minimumBet = minBets;
    }

    return urlFilters;
  }, [searchParams]);

  // Update internal state when URL changes
  useEffect(() => {
    const filtersFromUrl = getGamesFiltersFromUrl();
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setGameFiltersState(filtersFromUrl);
  }, [searchParams, getGamesFiltersFromUrl]);

  // Update URL with filters
  const updateUrlWithFilters = useCallback(
    (filters: GamesFiltersType) => {
      // Update internal state first
      setGameFiltersState(filters);

      const params = new URLSearchParams(searchParams);

      // Clear existing params
      params.delete('sorting');
      params.delete('gameProviders');
      params.delete('volatilityMin');
      params.delete('volatilityMax');
      params.delete('minimumBet');

      // Add new params based on filters
      if (filters.gameProviders && filters.gameProviders.length > 0) {
        params.set('gameProviders', filters.gameProviders.join(','));
      }

      if (filters.sorting) {
        params.set('sorting', filters.sorting);
      }

      if (filters.volatility) {
        params.set('volatilityMin', filters.volatility.min);
        params.set('volatilityMax', filters.volatility.max);
      }

      if (filters.minimumBet && filters.minimumBet.length > 0) {
        params.set('minimumBet', filters.minimumBet.join(','));
      }

      // Update URL without refreshing the page
      replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [searchParams, pathname, replace]
  );

  const getFiltersCount = useCallback((): number => {
    const params = new URLSearchParams(searchParams);
    let count = 0;

    // Handle volatility
    const minVol = params.get('volatilityMin');
    const maxVol = params.get('volatilityMax');
    if (minVol || maxVol) {
      count++;
    }

    // Handle gameProviders
    const providers = params.get('gameProviders')?.split(',') || [];
    if (providers.length > 0) {
      count += providers.length;
    }

    // Handle minimumBet
    const minBets = params.get('minimumBet')?.split(',') || [];
    if (minBets.length > 0) {
      count += minBets.length;
    }

    return count;
  }, [searchParams]);

  return {
    gameFilters: gameFiltersState,
    filtersCount: getFiltersCount(),
    updateGameFilters: updateUrlWithFilters,
  };
}

export { useGameFilters };
